<template>
  <div>
    <app-modal content-class="col-sm-6 col-lg-4 col-md-5 p-0" @close="close" overflow>
      <b-card no-body>
        <b-card-body class="pb-2" :title="isNew ? 'Create new floor' : 'Update floor'">
          <div class="panel-close d-md-none" @click="close"><i class="ion ion-md-close"></i></div>
          <b-form-group label="Name" label-cols="3" label-align="right" :state="$v.floor.name.required">
            <b-input :value="floor.name" @change="update('name', $event)" />
            <template #invalid-feedback v-if="!$v.floor.name.required">Floor name required</template>
          </b-form-group>
          <b-form-group label="Building" :label-cols="3" label-align="right">
            <b-row>
              <b-col cols="9">
                <b-select
                  :value="floor.building_id"
                  @change="update('building_id', $event)"
                  text-field="name"
                  :disabled="buildings.length === 0"
                  :options="buildings"
                  value-field="id"
                />
              </b-col>
              <b-col cols="3">
                <b-btn variant="primary" @click="buildingEditorToggle" v-text="floor.building_id ? `edit` : `add`"></b-btn>
              </b-col>
            </b-row>
          </b-form-group>
          <b-form-group label="Status" label-cols="3" label-align="right">
            <b-select :value="floor.status" @change="update('status', $event)" :options="{ 0: 'Draft', 1: 'Published' }" />
          </b-form-group>
          <b-form-group
            label="Map drawing"
            label-cols="3"
            label-align="right"
            description="Upload floor plan drawing in any format you have, pdf prefer"
          >
            <div :class="{ 'map-field': floor.drawing_file_id }">
              <span
                v-if="floor.drawing_file_name"
                v-text="floor.drawing_file_name"
                class="file-name"
                :title="floor.drawing_file_name"
              ></span>
              <app-file-upload
                ref="uploader"
                @upload="onUpload"
                @error="onError"
                post-url="/api/v1/files"
                auto-upload
                :short-view="Boolean(floor.drawing_file_id)"
                :short-view-message="uploadTitle"
              ></app-file-upload>
            </div>
          </b-form-group>
          <b-form-group label="Map status" label-align="right" label-cols="3">
            <div class="map-text">{{ mapStatus }}</div>
          </b-form-group>
        </b-card-body>
        <b-card-footer class="text-right">
          <b-btn variant="primary" @click="saveAndRequest" v-if="requestAvailable">Request map update</b-btn>&nbsp;
          <b-btn variant="primary" @click="save">Save</b-btn>&nbsp;
          <b-btn variant="default" @click="close">Cancel</b-btn>
        </b-card-footer>
      </b-card>
    </app-modal>

    <app-modal overflow v-if="buildingEditorEnabled" @close="() => (buildingEditorEnabled = false)">
      <building-list @close="() => (buildingEditorEnabled = false)"></building-list>
    </app-modal>
  </div>
</template>

<script>
import AppFileUpload from '@/components/shared/AppFileUpload'
import store from '@/store'
import { FLOOR_CREATE, FLOOR_UPDATE } from '@/store/modules/floor'
import { post } from '@/utils/Api'
import { isLocalId } from '@/utils/Id'
import BuildingList from '@/views/BuildingList'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'floor-edit',
  components: {
    BuildingList,
    AppFileUpload
  },
  data: () => ({
    buildingEditorEnabled: false,
    floorId: null
  }),
  validations() {
    return {
      floor: {
        name: {
          required
        }
      }
    }
  },
  computed: {
    ...mapGetters('floor', ['getFloor']),
    ...mapGetters('building', ['buildings']),
    uploadTitle() {
      return this.floor.drawing_file_id ? `<div style="font-size: 14px; margin: 3px 0 -2px 0">change</div>` : `click to upload new drawing`
    },
    floor() {
      return this.getFloor(this.floorId) || {}
    },
    isNew() {
      return isLocalId(this.floorId)
    },
    mapStatus() {
      if (this.floor.map_request_id) {
        return `generation is in progress...`
      } else if (this.floor.map_updated_at) {
        return `ready, updated at ${this.$options.filters.appDateTime(this.floor.map_updated_at)}`
      } else if (this.floor.drawing_file_id) {
        return `drawing uploaded, request a map update`
      }
      return `Upload a drawing first`
    },
    requestAvailable() {
      return !this.floor.map_request_id && this.floor.drawing_file_id
    }
  },
  methods: {
    ...mapMutations('floor', { updateLocal_: FLOOR_UPDATE }),
    ...mapActions('floor', ['floorSave', 'floorDestroy', 'floorLoad']),
    buildingEditorToggle() {
      this.buildingEditorEnabled = !this.buildingEditorEnabled
    },
    onUpload(file) {
      this.update('drawing_file_id', file.id)
      this.update('drawing_file_name', file.name)
      this.$appToast.success('Map source file uploaded successfully')
    },
    onError() {
      this.$appToast.error('Map source file upload fails for unknown reason')
    },
    update(k, v) {
      let value = v instanceof Event ? v.target.value : v
      this.updateLocal_({ id: this.floor.id, [k]: value })
    },
    close() {
      this.$router.push({ name: 'floors' })
    },
    save(close = true) {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        return this.floorSave({ floor: this.floor, config: { params: { expand: 'drawing_file_name' } } }).then((fId) => {
          close && this.close()
          this.floorId = fId
          return fId
        })
      }
    },
    saveAndRequest() {
      this.save(false).then((fId) => {
        post(`/map-requests`, { floor_id: fId }).then((r) => {
          let requestId = r.data.id
          if (requestId) {
            this.update('map_request_id', requestId)
            this.save()
            this.$appToast.info('Map generation request created!')
          }
        })
      })
    }
  },
  created() {
    this.floorId = this.$route.params.floorId
  },
  beforeRouteEnter(to, from, next) {
    // create local floor
    let fId = to.params.floorId
    if (isLocalId(fId) && !store.getters['floor/getFloor'](fId)) {
      store.commit(`floor/${FLOOR_CREATE}`, { id: fId })
    } else {
      store.dispatch(`floor/floorLoad`, { id: fId, params: { expand: 'drawing_file_name' } })
    }
    next()
  }
}
</script>

<style lang="scss">
.panel-close {
  position: absolute;
  right: 25px;
  top: 20px;
  font-size: 24px;
  cursor: pointer;
}

.map-field {
  display: flex;
  align-items: center;
  justify-content: left;
  font-size: 14px;
  margin-right: 10px;
}

.map-upload-message {
  font-size: 14px;
  margin-right: 10px;

  i {
    margin-top: 5px;
    font-size: 18px;
  }
}

.map-text {
  min-height: calc(2.25rem + 2px);
  margin-top: 8px;
}

.file-name {
  padding-top: 4px;
  padding-right: 5px;
  max-width: 320px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
